import React, { useContext } from 'react'
import styled from 'styled-components'

import { LaunchSketchAlert, useLicenseAgreement } from '@sketch/modules-common'
import {
  DocumentDownloadFragment,
  ShareInfoFragment,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { DownloadProps } from '../../utils'
import {
  useBreakpoint,
  Dropdown,
  ModalContext,
  Flex,
  downloadFile,
} from '@sketch/components'

import AppLogo from '@sketch/icons/logo-2x.png'
import { ReactComponent as EditIcon } from '@sketch/icons/pen-nib-17'
import { ReactComponent as DownloadIcon } from '@sketch/icons/arrow-down-to-line-17'

const AppIcon = styled.img.attrs({ src: AppLogo })`
  width: 17px;
  height: 17px;
  margin-left: 4px;
`

export interface ShareForDownloadOptions
  extends Pick<
    ShareInfoFragment,
    'userCanUpdateSettings' | 'identifier' | 'userCanEdit' | 'shareType'
  > {}

interface DownloadOptionsProps {
  share: ShareForDownloadOptions
  download: DownloadProps
  document: DocumentDownloadFragment
  isViewingLatestVersion?: boolean
  workspaceStatus?: WorkspaceMinimalFragment['status']
}

const DownloadOptions: React.FCC<DownloadOptionsProps> = ({
  share,
  document,
  download,
  isViewingLatestVersion,
  workspaceStatus,
}) => {
  const { showModal } = useContext(ModalContext)
  const isDesktop = useBreakpoint('sm')

  const { hasLicenseAgreement, showLicenseAgreementModal } =
    useLicenseAgreement({
      shareIdentifier: share.identifier,
    })

  const isActive = workspaceStatus === 'ACTIVE'
  // if there is no workspace status, it's because it's a public library
  const isPublic = workspaceStatus === undefined

  const showLibraryItem =
    share.shareType === 'LIBRARY' && (isActive || isPublic)

  const showOpenInSketchItem = document?.userCanOpenInApp || false
  const isTemplate = share.shareType === 'TEMPLATE'

  const elements: React.ReactNode[] = []

  if (showLibraryItem && isDesktop) {
    elements.push(
      <Dropdown.Item
        key={2}
        onClick={() =>
          showLicenseAgreementModal({
            agreeAction: () => {
              showModal(LaunchSketchAlert, {
                shareId: share.identifier,
                action: 'addLibrary',
              })
            },
            helpText:
              'Read and agree to the License Agreement to add this Library.',
          })
        }
      >
        Add Library
      </Dropdown.Item>
    )
  }

  if (showOpenInSketchItem && isViewingLatestVersion && isDesktop)
    elements.push(
      <Dropdown.Item
        icon={<EditIcon />}
        key={0}
        onClick={event => {
          event.preventDefault()

          showModal(LaunchSketchAlert, {
            shareId: share.identifier,
            action: 'openInSketch',
          })
        }}
      >
        <Flex alignItems="center">
          {isTemplate ? (
            'New from Template'
          ) : (
            <>
              Edit in <AppIcon alt="Sketch" />
            </>
          )}
        </Flex>
      </Dropdown.Item>
    )

  if (download.isAvailable) {
    hasLicenseAgreement
      ? elements.push(
          <Dropdown.Item
            icon={<DownloadIcon />}
            key={1}
            data-testid="download-document-license-agreement"
            onClick={() =>
              showLicenseAgreementModal({
                agreeAction: () => downloadFile(download.url),
                helpText:
                  'Read and agree to the License Agreement to download this Document.',
              })
            }
          >
            Download
          </Dropdown.Item>
        )
      : elements.push(
          <Dropdown.Download
            icon={<DownloadIcon />}
            key={1}
            href={download.url}
          >
            <Flex alignItems="center">Download</Flex>
          </Dropdown.Download>
        )
  }

  if (elements.length <= 0) return null
  return <>{elements}</>
}

export default DownloadOptions
